import { useEffect, useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Link,
  Stack,
  Tooltip as MuiTooltip,
  Typography,
  useTheme,
} from '@mui/material'
import {
  Area,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { ChartEmpty } from '../components/ChartEmpty'
import { ComparacaoSelect } from '../components/ComparacaoSelect'
import { KpiCard } from '../components/KpiCard'
import { MiniPnlCard } from '../components/MiniPnlCard'
import { OpsAlertsCard } from '../components/OpsAlertsCard'
import { AiInsightPanel } from '../components/AiInsightPanel'
import { useBiData } from '../state/BiDataContext'
import { canalFilterLabel, channelNameToId, fmtBrl } from '../api/bridge'
import {
  labelModo,
  mcPonderada,
  rotularJanela,
  variacaoPct,
  type ModoComparacao,
} from './home/comparacao'
import { useComparacaoHome } from './home/useComparacaoHome'
import { ressalvasDaMargem } from './home/ressalvas'
import { TabelaPorDia } from './home/TabelaPorDia'

function fmtAxis(n: number): string {
  const abs = Math.abs(n)
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (abs >= 1000) return `${(n / 1000).toFixed(abs >= 10_000 ? 0 : 1)}k`
  return String(Math.round(n))
}

function fmtPct(n: number | null): string {
  return n == null ? '—' : `${n.toFixed(1).replace('.', ',')}%`
}

/** Diferença em pontos percentuais — MC % contra MC %, nunca variação relativa. */
function fmtPontos(atual: number | null, anterior: number | null): string | null {
  if (atual == null || anterior == null) return null
  const d = atual - anterior
  const sinal = d > 0 ? '+' : ''
  return `${sinal}${d.toFixed(1).replace('.', ',')} p.p.`
}

export function DashboardPage() {
  const theme = useTheme()
  const chart = theme.chart
  const {
    data,
    loading,
    error,
    refresh,
    marketplace,
    storeKey,
    canalSelect,
    customRange,
    setMarketplace,
  } = useBiData()
  const canalLabel = canalFilterLabel(canalSelect)

  const [modo, setModo] = useState<ModoComparacao>('anterior')

  // Refaz a comparação quando uma carga do overview termina — sem isto o
  // "Atualizar" da barra atualizaria o período e deixaria a janela comparada
  // velha na tela, lado a lado com o número novo.
  const [tick, setTick] = useState(0)
  useEffect(() => {
    if (!loading) setTick((t) => t + 1)
  }, [loading])

  const inicio = data?.rangeInicio || customRange?.inicio || ''
  const fim = data?.rangeFim || customRange?.fim || ''
  const comp = useComparacaoHome(inicio, fim, modo, marketplace, tick)

  const ticketMedio = data && data.pedidos > 0 ? data.vendas / data.pedidos : 0
  const ticketComparado =
    comp.vendas != null && comp.pedidos != null && comp.pedidos > 0
      ? comp.vendas / comp.pedidos
      : null

  // MC % ponderada: Σ margem ÷ Σ receita dos pedidos que têm margem. Média de
  // percentuais daria o mesmo peso a um pedido de R$ 12 e a um de R$ 12.000.
  const mcPctAtual = data ? mcPonderada(data.margemTotal, data.receitaComMargem) : null
  const mcPctComparada = mcPonderada(comp.margem ?? NaN, comp.receitaComMargem ?? NaN)

  const naoValidos =
    data && data.pedidosTotais > data.pedidos ? data.pedidosTotais - data.pedidos : null

  const ressalvas = useMemo(() => ressalvasDaMargem(data), [data])
  const sufixoDelta = `vs ${labelModo(modo).toLowerCase().replace('mesmo período do ', '')}`

  const sparkVendas = useMemo(() => (data?.daily || []).map((d) => d.valor), [data?.daily])
  const sparkPedidos = useMemo(() => (data?.daily || []).map((d) => d.pedidos), [data?.daily])
  const sparkTicket = useMemo(
    () => (data?.daily || []).map((d) => (d.pedidos > 0 ? d.valor / d.pedidos : 0)),
    [data?.daily],
  )

  /**
   * As duas séries alinhadas **pela posição do dia**, não pela data: o dia 1 do
   * período contra o dia 1 da janela comparada. É como o Upseller desenha, e é
   * o único alinhamento que funciona quando as janelas caem em dias diferentes
   * da semana. Quando as durações não batem, sobra `null` — que o Recharts não
   * desenha, em vez de fingir um zero.
   */
  const serieGrafico = useMemo(() => {
    const atual = data?.daily || []
    const outra = comp.serie
    const n = Math.max(atual.length, outra.length)
    return Array.from({ length: n }, (_, i) => ({
      dia: atual[i]?.dia ?? '',
      atual: atual[i] ? atual[i].valor : null,
      comparado: outra[i] ? outra[i].valor : null,
      diaComparado: outra[i]?.data ?? '',
    }))
  }, [data?.daily, comp.serie])

  const linhas = useMemo(
    () =>
      (data?.daily || []).map((d) => ({
        data: d.data,
        dia: d.dia,
        itens: d.itens ?? null,
        mc: d.mc ?? null,
        mcAj: d.mcAj ?? null,
        recMc: d.recMc ?? null,
        valor: d.valor,
        pedidos: d.pedidos,
      })),
    [data?.daily],
  )

  const canais = useMemo(
    () => (data?.canais || []).map((c) => ({ name: c.name, value: c.value })),
    [data?.canais],
  )
  const canalTotal = canais.reduce((s, c) => s + c.value, 0)

  const selectCanal = (name: string) => {
    const id = channelNameToId(name)
    if (!id) return
    setMarketplace(marketplace === id ? '' : id)
  }

  const rotuloJanela = rotularJanela(comp.janela)
  const semComparacao = comp.erro || (!comp.carregando && comp.vendas == null)

  return (
    <Stack spacing={3}>
      {error ? (
        <Alert
          severity="error"
          action={
            <Typography
              component="button"
              onClick={refresh}
              sx={{
                border: 0,
                bgcolor: 'transparent',
                cursor: 'pointer',
                color: 'inherit',
                fontWeight: 700,
              }}
            >
              Tentar de novo
            </Typography>
          }
        >
          {error}
        </Alert>
      ) : null}

      {loading && !data ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : null}

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ md: 'flex-end' }}
        spacing={1.5}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
          >
            Vendas Diárias
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data?.rangeLabel ? `Período ${data.rangeLabel}` : 'Período selecionado'}
            {comp.janela ? ` · comparado com ${rotuloJanela}` : ''}
            {marketplace || storeKey ? ` · ${canalLabel}` : ' · todos os canais'}
          </Typography>
        </Box>
        <ComparacaoSelect
          value={modo}
          onChange={setModo}
          disabled={loading && !data}
          sx={{ minWidth: 260 }}
        />
      </Stack>

      {semComparacao && data ? (
        <Alert severity="info" variant="outlined">
          Sem dados na janela de comparação ({rotuloJanela}) — os cards abaixo mostram o período
          sozinho, sem variação.
        </Alert>
      ) : null}

      <Grid container spacing={{ xs: 1.25, sm: 2 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <KpiCard
            label="Vendas válidas"
            value={data?.vendasFmt ?? '—'}
            hint={comp.vendas != null ? `${fmtBrl(comp.vendas)} em ${rotuloJanela}` : 'Sem comparação'}
            deltaPct={data ? variacaoPct(data.vendas, comp.vendas) : null}
            deltaSufixo={sufixoDelta}
            sparkline={sparkVendas}
            loading={loading && !data}
            to="/produtos"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <KpiCard
            label="Pedidos válidos"
            value={data ? data.pedidos.toLocaleString('pt-BR') : '—'}
            hint={
              comp.pedidos != null
                ? `${comp.pedidos.toLocaleString('pt-BR')} em ${rotuloJanela}`
                : 'Sem comparação'
            }
            deltaPct={data ? variacaoPct(data.pedidos, comp.pedidos) : null}
            deltaSufixo={sufixoDelta}
            sparkline={sparkPedidos}
            loading={loading && !data}
            to="/pedidos"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <KpiCard
            label="Ticket médio"
            value={data ? fmtBrl(ticketMedio) : '—'}
            hint={
              ticketComparado != null
                ? `${fmtBrl(ticketComparado)} em ${rotuloJanela}`
                : 'Vendas ÷ pedidos válidos'
            }
            deltaPct={data ? variacaoPct(ticketMedio, ticketComparado) : null}
            deltaSufixo={sufixoDelta}
            sparkline={sparkTicket}
            loading={loading && !data}
            to="/pedidos"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <KpiCard
            label="Margem de contribuição"
            value={data?.cmvInconsistente ? 'Inconsistente' : (data?.margemFmt ?? '—')}
            hint={
              comp.margem != null ? `${fmtBrl(comp.margem)} em ${rotuloJanela}` : 'Soma do período'
            }
            badge={ressalvas[0]?.selo ?? null}
            valueTone={ressalvas.length ? 'caveat' : 'default'}
            deltaPct={
              data && !data.cmvInconsistente ? variacaoPct(data.margemTotal, comp.margem) : null
            }
            deltaSufixo={sufixoDelta}
            loading={loading && !data}
            to="/margens"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <KpiCard
            label="MC %"
            value={data?.cmvInconsistente ? '—' : fmtPct(mcPctAtual)}
            hint={
              mcPctComparada != null
                ? `${fmtPct(mcPctComparada)} em ${rotuloJanela} · ${fmtPontos(mcPctAtual, mcPctComparada) ?? '—'}`
                : 'Σ margem ÷ Σ receita dos pedidos com margem'
            }
            badge={ressalvas.length ? 'Com ressalva' : null}
            valueTone={ressalvas.length ? 'caveat' : 'default'}
            loading={loading && !data}
            to="/margens"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <KpiCard
            label="Pedidos não válidos"
            value={naoValidos != null ? naoValidos.toLocaleString('pt-BR') : '—'}
            hint={
              data && data.pedidosTotais > 0
                ? `${data.pedidosTotais.toLocaleString('pt-BR')} pedidos no total do período`
                : 'Total do período não informado pela fonte'
            }
            loading={loading && !data}
            to="/pedidos"
          />
        </Grid>
      </Grid>

      {ressalvas.length ? (
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: -1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', mr: 0.5 }}>
            Ressalvas da margem:
          </Typography>
          {ressalvas.map((r) => (
            <MuiTooltip key={r.selo} title={r.detalhe}>
              <Chip
                component={RouterLink}
                to="/margens"
                clickable
                size="small"
                label={r.selo}
                color={r.nivel === 'grave' ? 'warning' : 'default'}
                variant="outlined"
                sx={{ textDecoration: 'none', fontWeight: 600 }}
              />
            </MuiTooltip>
          ))}
        </Stack>
      ) : null}

      <Grid container spacing={2} alignItems="flex-start">
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="baseline"
                spacing={1}
                sx={{ mb: 0.5 }}
              >
                <Typography variant="h6">Vendas · período vs comparação</Typography>
                <Link
                  component={RouterLink}
                  to="/produtos"
                  underline="hover"
                  variant="body2"
                  fontWeight={700}
                >
                  Ver por anúncio
                </Link>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Linha cheia: período selecionado. Tracejada: {labelModo(modo).toLowerCase()} (
                {rotuloJanela}). Os dias estão alinhados por posição, não por data.
              </Typography>
              <Box sx={{ width: '100%', height: { xs: 220, sm: 280 } }}>
                {serieGrafico.length ? (
                  <ResponsiveContainer>
                    <ComposedChart data={serieGrafico} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="homeVendasFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={chart.fill} stopOpacity={0.4} />
                          <stop offset="100%" stopColor={chart.fill} stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={theme.palette.divider}
                        vertical={false}
                      />
                      <XAxis
                        dataKey="dia"
                        tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tickFormatter={fmtAxis}
                        tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                        axisLine={false}
                        tickLine={false}
                        width={44}
                      />
                      <Tooltip
                        formatter={(value, name) => [
                          fmtBrl(Number(value)),
                          name === 'atual' ? 'Período' : 'Comparação',
                        ]}
                        contentStyle={{
                          borderRadius: 8,
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="atual"
                        stroke={chart.line}
                        fill="url(#homeVendasFill)"
                        strokeWidth={2.5}
                        connectNulls={false}
                        activeDot={{ r: 4, strokeWidth: 0 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="comparado"
                        stroke={theme.palette.text.secondary}
                        strokeWidth={1.75}
                        strokeDasharray="5 4"
                        dot={false}
                        connectNulls={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <ChartEmpty title="Sem série diária no período." dense />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Stack spacing={2}>
            <Card>
              <CardContent>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="baseline"
                  sx={{ mb: 0.5 }}
                >
                  <Typography variant="h6">Por canal</Typography>
                  {canalTotal > 0 ? (
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {fmtBrl(canalTotal)}
                    </Typography>
                  ) : null}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Clique para filtrar{marketplace || storeKey ? ` · ${canalLabel}` : ''}
                </Typography>
                <Box sx={{ width: '100%', height: { xs: 150, sm: 160 }, position: 'relative' }}>
                  {canais.length ? (
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={canais}
                          dataKey="value"
                          nameKey="name"
                          innerRadius={40}
                          outerRadius={62}
                          paddingAngle={2}
                          stroke="none"
                          cursor="pointer"
                          onClick={(_, index) => {
                            const row = canais[index]
                            if (row?.name) selectCanal(String(row.name))
                          }}
                        >
                          {canais.map((c, i) => (
                            <Cell
                              key={c.name}
                              fill={chart.pie[i % chart.pie.length]}
                              opacity={
                                !marketplace || channelNameToId(c.name) === marketplace ? 1 : 0.35
                              }
                            />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => fmtBrl(Number(v))} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <ChartEmpty title="Sem vendas por canal." dense />
                  )}
                </Box>
                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                  {canais.slice(0, 5).map((c, i) => {
                    const pct = canalTotal ? (c.value / canalTotal) * 100 : 0
                    const id = channelNameToId(c.name)
                    const active = marketplace && id === marketplace
                    return (
                      <Stack
                        key={c.name}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        onClick={() => selectCanal(c.name)}
                        sx={{
                          cursor: id ? 'pointer' : 'default',
                          borderRadius: 1,
                          px: 0.5,
                          py: 0.15,
                          bgcolor: active ? 'action.selected' : 'transparent',
                          '&:hover': id ? { bgcolor: 'action.hover' } : undefined,
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: chart.pie[i % chart.pie.length],
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="body2" noWrap fontWeight={active ? 700 : 400}>
                            {c.name}
                          </Typography>
                        </Stack>
                        <Typography
                          variant="caption"
                          sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                        >
                          {pct.toFixed(0)}%
                        </Typography>
                      </Stack>
                    )
                  })}
                </Stack>
              </CardContent>
            </Card>

            <AiInsightPanel
              dominio="home"
              fallback={{
                title: data
                  ? `Receita ${fmtBrl(data.vendas)} · ${data.pedidos} pedidos`
                  : 'Insight da IA do período',
                detail: 'Leitura geral da performance — a mesma que a Fiesta IA usa no chat.',
                recommendations: [
                  { title: 'Ver margens', detail: 'Margem por pedido e por anúncio.', to: '/margens' },
                  { title: 'Ver pedidos', detail: 'Funil e lista do período.', to: '/pedidos' },
                ],
              }}
            />
          </Stack>
        </Grid>
      </Grid>

      <TabelaPorDia linhas={linhas} />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5 }}>
          <MiniPnlCard data={data} loading={loading && !data} />
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <OpsAlertsCard />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  )
}
